import AWS from 'aws-sdk';
import { AWS_API } from 'config';
import { getTemporaryAWSCredentials } from './loginHelpers';
import { IRecordFile } from 'ui-component/RecordView/types';
import { SignedUrl } from 'views/DynamicPortals/types';
import { getFilenameWithoutExtension } from './stringHelpers';

const EXT_TO_MIME: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    csv: 'text/csv',
    txt: 'text/plain'
};

const contentTypeForUpload = (file: File, extension: string): string => {
    if (file.type) return file.type;
    return EXT_TO_MIME[extension.toLowerCase()] ?? 'application/octet-stream';
};

/** For signed GETs of images: align response Content-Type with image/* to reduce cross-origin ORB blocks in <img>. */
const responseContentTypeForS3Key = (key: string): string | undefined => {
    const ext = key.split('.').pop()?.toLowerCase().split('?')[0] ?? '';
    const mime = EXT_TO_MIME[ext];
    return mime?.startsWith('image/') ? mime : undefined;
};

export type CSVOpportunity = {
    opportunityName?: string;
    description?: string;
    spaOppNumber?: string;
    account?: string;
    accountType?: string;
    spaStatus?: string;
    contactName?: {
        last?: string;
        first?: string;
    };
    contactEmail?: string;
    estimatedClose?: string;
    totalNet?: string;
    customerNumber?: string;
    productList?: string;
    createDate?: string;
    spaApprovalDate?: string;
    owner?: string;
    accountManager?: string;
    city?: string;
    state?: string;
    status?: string;
    federal?: string;
    repIndividualUserID?: string;
    customer?: string;
};

export type CSVDealerOption = {
    opportunityId: string;
    customer: string;
    option: string;
    tenant_id: string;
};

/**
 * @function S3Uploader
 * @description Upload a file to AWS S3.
 * @param {File} file - The file to upload.
 * @param {String} fileName - The file name to upload SHOULD BE AN UUID.
 * @returns {Promise<string>} - The key of the file in S3.
 */
export const S3Uploader = async (file: File, fileName?: string): Promise<string> => {
    const credentials = await getTemporaryAWSCredentials();
    const tenantId = localStorage.getItem('tenant_id');
    const fileExtension = file.name.split('.')?.slice(-1)[0];
    const fileOriginalName = fileName || getFilenameWithoutExtension(file);

    if (!fileExtension) throw new Error('invalid file type');

    // AWS S3 Configuration
    AWS.config.update({
        sessionToken: credentials.sessionToken,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
    });
    const myBucket = new AWS.S3({
        params: { Bucket: AWS_API.s3Bucket },
        region: AWS_API.region
    });
    const key = `${tenantId}/${fileOriginalName}.${fileExtension}`;
    const params = {
        Body: file,
        Bucket: AWS_API.s3Bucket,
        Key: key,
        ContentType: contentTypeForUpload(file, fileExtension)
    };

    await myBucket.putObject(params).promise();

    return key;
};

/**
 * @function decodeUrlEncodedKey
 * @description Decode a URL-encoded S3 key. Returns the original key if decoding fails.
 * @param {string} key - The URL-encoded key to decode.
 * @returns {string} - The decoded key, or the original key on error.
 */
const decodeUrlEncodedKey = (key: string): string => {
    try {
        return decodeURIComponent(key);
    } catch {
        return key;
    }
}

/**
 * @function cleanS3ImageUrl
 * @description Normalize an S3 URL or key to a decoded key (path without domain/query). Handles full URLs or raw keys.
 * @param {string} urlOrKey - Full S3 URL (http/https) or a raw S3 key.
 * @returns {string} - The decoded S3 key, or empty string for invalid input.
 */
export const cleanS3ImageUrl = (urlOrKey: string): string => {
    if (!urlOrKey || typeof urlOrKey !== 'string') return '';
    const trimmed = urlOrKey.trim();
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
        return decodeUrlEncodedKey(trimmed);
    }
    try {
        const parsed = new URL(trimmed);
        const key = parsed.pathname.slice(1); // remove leading slash
        return decodeUrlEncodedKey(key);
    } catch {
        return trimmed;
    }
};

/**
 * @function cleanS3UrlsRecursive
 * @description Recursively walk an object/array and replace any string at key "url" with its cleaned S3 key via cleanS3ImageUrl.
 * @param {unknown} value - Any value (object, array, or primitive).
 * @returns {unknown} - A copy of the value with "url" string values cleaned.
 */
function cleanS3UrlsRecursive(value: unknown): unknown {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(cleanS3UrlsRecursive);
    }
    const obj = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (key === 'url' && typeof val === 'string') {
            result[key] = cleanS3ImageUrl(val);
        } else {
            result[key] = cleanS3UrlsRecursive(val);
        }
    }
    return result;
}

/**
 * @function cleanS3UrlsInValue
 * @description Parse a JSON string, recursively clean all "url" values to S3 keys, and return the result as JSON.
 * @param {string} jsonString - JSON string that may contain "url" fields (e.g. S3 URLs or keys).
 * @returns {string} - JSON string with "url" values cleaned to S3 keys, or the original string if parse fails.
 */
export const cleanS3UrlsInValue = (jsonString: string): string => {
    if (!jsonString || typeof jsonString !== 'string') return jsonString ?? '';
    try {
        const parsed = JSON.parse(jsonString) as unknown;
        const cleaned = cleanS3UrlsRecursive(parsed);
        return JSON.stringify(cleaned);
    } catch {
        return jsonString;
    }
};

/**
 * @function S3ImgUrlGetter
 * @description Get the image URL from AWS S3.
 * @param {string} key - The key of the image in AWS S3.
 * @returns {Promise<string>} - The URL of the image.
 */
export const S3ImgUrlGetter = async (key: string): Promise<string> => {
    const credentials = await getTemporaryAWSCredentials();
    // AWS S3 Configuration
    AWS.config.update({
        sessionToken: credentials.sessionToken,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
    });
    const myBucket = new AWS.S3({
        params: { Bucket: AWS_API.s3Bucket },
        region: AWS_API.region
    });

    const result = await myBucket.getSignedUrlPromise('getObject', { Bucket: AWS_API.s3Bucket, Key: key });
    return result;
};

/**
 * @function S3UrlGetter
 * @description Get the image URL from AWS S3.
 * @param {string} key - The key of the image in AWS S3.
 * @returns {Promise<string>} - The URL of the image.
 */
export const S3UrlGetter = async (key: string): Promise<string> => {
    const credentials = await getTemporaryAWSCredentials();
    // AWS S3 Configuration
    AWS.config.update({
        sessionToken: credentials.sessionToken,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
    });
    const myBucket = new AWS.S3({
        params: { Bucket: AWS_API.s3Bucket },
        region: AWS_API.region,
        signatureVersion: 'v4'
    });

    const responseContentType = responseContentTypeForS3Key(key);
    const result = await myBucket.getSignedUrlPromise('getObject', {
        Bucket: AWS_API.s3Bucket,
        Key: key,
        ...(responseContentType ? { ResponseContentType: responseContentType } : {})
    });
    return result;
};

/**
 * @function S3MultipleImgUrlGetter
 * @description Get the image URL from AWS S3.
 * @param {{ s3Url: string, index: number }[]} items - Array of keys AWS S3.
 * @returns {Promise<SignedUrl[]>} - The list of URLs of the image.
 */
export const S3MultipleImgUrlGetter = async (items: { s3Url: string; index: number }[]): Promise<SignedUrl[]> => {
    const credentials = await getTemporaryAWSCredentials();
    // AWS S3 Configuration
    AWS.config.update({
        sessionToken: credentials.sessionToken,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
    });
    const myBucket = new AWS.S3({
        params: { Bucket: AWS_API.s3Bucket },
        region: AWS_API.region
    });

    const signedUrlList = await Promise.all(
        items.map(async (rawUrl) => {
            const result: SignedUrl = { signedUrl: '', index: rawUrl.index };
            if (rawUrl.s3Url) {
                const url = new URL(rawUrl.s3Url);
                const key = url.pathname.slice(1);

                result.signedUrl = await myBucket.getSignedUrlPromise('getObject', { Bucket: AWS_API.s3Bucket, Key: key });
            }
            return result;
        })
    );

    return signedUrlList;
};

/**
 * Retrieves a CSV file from S3 and maps its contents to an array of CSVOpportunity objects.
 *
 * @return {Promise<CSVOpportunity[]>} - A promise that resolves to an array of CSVOpportunity objects.
 */
export const S3CSVOpportunityMapper = async (): Promise<CSVOpportunity[]> => {
    const tenantId = localStorage.getItem('tenant_id');
    const fileUrl = await S3ImgUrlGetter(`ais/${tenantId}.csv`);
    const file = await fetch(fileUrl);

    // USE THIS ONLY FOR TESTING PURPOSES OR IF S3 IS DOWN
    // const file = await fetch('/AllOpportunitiesNew.csv');

    const blob = await file.text();
    const opportunities: CSVOpportunity[] = [];
    blob.split('\n').forEach((line, index) => {
        if (index > 0) {
            // Get all qouted parts in order to prevent comma injection
            const re = /"(.*?)"/g;
            const result: string[] = [];
            let current;
            while ((current = re.exec(line))) {
                result.push(current?.pop() ?? '');
            }
            let lineParsed = line;
            result.forEach((part) => {
                lineParsed = lineParsed.replaceAll(part, part.replaceAll(',', ';'));
            });
            const opportunity = lineParsed.split(',');
            const contactName = opportunity[4]?.split(';') ?? ['', ''];
            opportunities.push({
                opportunityName: opportunity[0]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                spaOppNumber: opportunity[1]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                account: opportunity[2]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                accountType: opportunity[3]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                contactName: {
                    last: contactName[0]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                    first: contactName[1]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? ''
                },
                spaStatus: opportunity[6]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                accountManager: opportunity[7]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                totalNet: opportunity[8]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                customerNumber: opportunity[9]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                productList: opportunity[10]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                owner: opportunity[13]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                city: opportunity[10]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                state: opportunity[11]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                status: opportunity[12]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                federal: opportunity[18]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                repIndividualUserID: opportunity[19]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                customer: opportunity[20]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? ''
            });
        }
    });

    return opportunities;
};

/**
 * Retrieves a CSV file from S3 and maps its contents to an array of CSVDealerOption objects.
 *
 * @return {Promise<CSVOpportunity[]>} - A promise that resolves to an array of CSVOpportunity objects.
 */
export const S3CSVDealerOptionsMapper = async (): Promise<CSVDealerOption[]> => {
    const tenantId = localStorage.getItem('tenant_id');
    const fileUrl = await S3ImgUrlGetter(`ais/${tenantId}-dealer-options.csv`);
    const file = await fetch(fileUrl);

    // USE THIS ONLY FOR TESTING PURPOSES OR IF S3 IS DOWN
    // const file = await fetch('/67-dealer-options.csv');

    const blob = await file.text();
    const list: CSVDealerOption[] = [];
    blob.split('\n').forEach((line, index) => {
        if (index > 0) {
            // Get all qouted parts in order to prevent comma injection
            const re = /"(.*?)"/g;
            const result: string[] = [];
            let current;
            while ((current = re.exec(line))) {
                result.push(current?.pop() ?? '');
            }
            let lineParsed = line;
            result.forEach((part) => {
                lineParsed = lineParsed.replaceAll(part, part.replaceAll(',', ';'));
            });
            const option = lineParsed.split(',');
            list.push({
                opportunityId: option[0]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                customer: option[1]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                option: option[2]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? '',
                tenant_id: option[3]?.replaceAll('"', '').replaceAll(';', ',').trim() ?? ''
            });
        }
    });

    return list;
};

export const getDocumentFileUrl = (recordFile: IRecordFile) => {
    try {
        const buffer = Buffer.from(recordFile.fileEncoded, 'base64');
        const blob = new Blob([buffer as BlobPart], { type: 'application/pdf' });

        return URL.createObjectURL(blob);
    } catch (err) {
        console.log(err);
        return '';
    }
};
export const getDocumentFileBlob = (recordFile: IRecordFile) => {
    try {
        const buffer = Buffer.from(recordFile.fileEncoded, 'base64');
        return new Blob([buffer as BlobPart], { type: 'application/pdf' });
    } catch (err) {
        console.log(err);
        return null;
    }
};
